<template>
  <div class="aiml-page">
    <!-- Carousel Hero Section -->
    <HeroCarouselComp 
      :slides="aimlSlides"
      background-image="/banner/banner-three.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="aimlMissionVision"
    />

    <!-- Trusted Companies Section -->
    <AIMLItsTrustComp :content="trustContent" />

    <!-- How It Works Section -->
    <AIMLItsHowWorkComp :content="howWorkContent" />

    <!-- Explore Our Services Section -->
    <AIMLItsServicesComp :content="servicesContent" />

    <!-- Client Testimonials Section -->
    <AIMLItsTestimonialComp :content="testimonialsContent" />

    <!-- Pricing Section -->
    <AIMLItsPricingComp :content="pricingContent" />

    <!-- Blogs & Articles Section -->
    <AIMLItsBlogsComp :content="blogsContent" />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import HeroCarouselComp from '@/components/HeroCarouselComp.vue'
import AIMLItsTrustComp from '@/components/AIMLItsTrustComp.vue'
import AIMLItsHowWorkComp from '@/components/AIMLItsHowWorkComp.vue'
import AIMLItsServicesComp from '@/components/AIMLItsServicesComp.vue'
import AIMLItsTestimonialComp from '@/components/AIMLItsTestimonialComp.vue'
import AIMLItsPricingComp from '@/components/AIMLItsPricingComp.vue'
import AIMLItsBlogsComp from '@/components/AIMLItsBlogsComp.vue'

export default {
  name: 'AIML',
  components: {
    HeroCarouselComp,
    AIMLItsTrustComp,
    AIMLItsHowWorkComp,
    AIMLItsServicesComp,
    AIMLItsTestimonialComp,
    AIMLItsPricingComp,
    AIMLItsBlogsComp
  },
  setup() {
    const companiesSwiper = ref(null)
    const servicesSwiper = ref(null)
    const testimonialsSwiper = ref(null)
    const isMonthly = ref(true)

    // Trusted Companies content
    const trustContent = ref({
      header: {
        title: "Trusted by the world's largest businesses"
      },
      companies: [
        {
          name: "Microsoft",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Microsoft_logo_%282012%29.svg/2048px-Microsoft_logo_%282012%29.svg.png"
        },
        {
          name: "Google",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/2560px-Google_2015_logo.svg.png"
        },
        {
          name: "Amazon",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/2560px-Amazon_logo.svg.png"
        },
        {
          name: "Apple",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/814px-Apple_logo_black.svg.png"
        },
        {
          name: "Meta",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Facebook_Logo_%282019%29.png/1200px-Facebook_Logo_%282019%29.png"
        },
        {
          name: "Netflix",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/77/Netflix_logo.svg/2560px-Netflix_logo.svg.png"
        },
        {
          name: "Spotify",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Spotify_logo_without_text.svg/168px-Spotify_logo_without_text.svg.png"
        },
        {
          name: "Tesla",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Tesla_T_symbol.svg/2048px-Tesla_T_symbol.svg.png"
        }
      ]
    })

    // How It Works content
    const howWorkContent = ref({
      header: {
        tag: "HOW IT WORKS",
        title: "Building Success, One Step at a Time"
      },
      steps: [
        {
          icon: "fas fa-search-plus",
          title: "Discovery & Strategy",
          description: "Understanding your business needs, analyzing requirements, and designing tailored AI solutions that align with your objectives.",
          number: "Step 01."
        },
        {
          icon: "fas fa-cogs",
          title: "Development & Integration",
          description: "Building, training, and deploying AI models into your existing systems with seamless integration and comprehensive testing.",
          number: "Step 02."
        },
        {
          icon: "fas fa-handshake",
          title: "Optimization & Support",
          description: "Continuous monitoring, performance optimization, and ongoing support to ensure your AI solutions deliver maximum value.",
          number: "Step 03."
        }
      ]
    })

    // Services content
    const servicesContent = ref({
      header: {
        tag: "OUR BEST SERVICES",
        title: "Explore Our Services"
      },
      services: [
        {
          number: "01.",
          title: "AI-Powered Solutions",
          description: "Specialize in delivering AI-powered solutions that revolutionize how businesses operate by leveraging the latest artificial intelligence technologies.",
          features: ["Personalized Experience", "Process Automation", "Predictive Analytics"],
          buttonText: "Learn More",
          image: require('@/assets/images/resource/portfolio-01.jpg')
        },
        {
          number: "02.",
          title: "Machine Learning Models",
          description: "Develop custom machine learning models that analyze data patterns and provide intelligent insights to drive business growth and efficiency.",
          features: ["Data Analysis", "Model Training", "Performance Optimization"],
          buttonText: "Learn More",
          image: require('@/assets/images/resource/portfolio-02.jpg')
        },
        {
          number: "03.",
          title: "Deep Learning Systems",
          description: "Build advanced deep learning systems that can process complex data structures and provide sophisticated AI capabilities for your business needs.",
          features: ["Neural Networks", "Image Recognition", "Natural Language Processing"],
          buttonText: "Learn More",
          image: require('@/assets/images/resource/portfolio-03.jpg')
        }
      ]
    })

    // Testimonials content
    const testimonialsContent = ref({
      header: {
        tag: "CLIENT TESTIMONIALS",
        title: "Proven Impact Through Testimonial"
      },
      testimonials: [
        {
          quote: "Ainex has been a game-changer for our business. Their AI solutions helped us streamline operations and achieve a 30% increase in efficiency.",
          client: {
            name: "Leslie Alexander",
            position: "Founder & CTO",
            image: require('@/assets/images/resource/author-01.png')
          }
        },
        {
          quote: "The team at Ainex delivered a tailored AI solution that perfectly matched our needs. Their expertise and support were exceptional throughout the project.",
          client: {
            name: "Guy Hawkins",
            position: "CEO & Founder",
            image: require('@/assets/images/resource/author-02.png')
          }
        },
        {
          quote: "Working with Ainex transformed our data analytics capabilities. Their machine learning models provided insights we never knew were possible.",
          client: {
            name: "Bessie Cooper",
            position: "Data Director",
            image: require('@/assets/images/resource/author-03.png')
          }
        }
      ]
    })

    // Pricing content
    const pricingContent = ref({
      header: {
        tag: "OUR PRICING",
        title: "Flexible Pricing, Powerful Tangible Results",
        description: "Specialize in delivering AI-powered solutions that revolutionize how businesses operate and grow."
      },
      plans: [
        {
          name: "Basic",
          discount: "50% off",
          price: {
            monthly: "20",
            yearly: "200"
          },
          description: "Specialize in delivering AI-powered solutions that revolutionize your business operations.",
          buttonText: "Choose Package",
          buttonType: "outline",
          features: [
            "AI-powered tools for businesses",
            "10 hours of tech support",
            "Basic data analysis",
            "Email support",
            "Access to online resources",
            "Reporting & Tutorials"
          ]
        },
        {
          name: "Pro Plan",
          popular: "Popular",
          featured: true,
          price: {
            monthly: "60",
            yearly: "600"
          },
          description: "Specialize in delivering AI-powered solutions that revolutionize your business operations.",
          buttonText: "Choose Package",
          buttonType: "primary",
          features: [
            "Everything in the Basic Plan",
            "Advanced AI solutions",
            "50 hours of tech support",
            "Customizable data dashboards",
            "Priority email & phone",
            "System optimizations"
          ]
        }
      ]
    })

    // Blogs content
    const blogsContent = ref({
      header: {
        tag: "RECENT BLOGS",
        title: "The Minds Behind Blog and News"
      },
      blogs: [
        {
          image: require('@/assets/images/resource/portfolio-01.jpg'),
          category: "Quantum",
          meta: "By Brandon • Jan 10, 2025",
          title: "Unlocking the Power of Data for Business Success",
          readMoreText: "Read More"
        },
        {
          image: require('@/assets/images/resource/portfolio-02.jpg'),
          category: "Innovate",
          meta: "By Brandon • Jan 10, 2025",
          title: "The Future of Work: Embracing Digital Transformation",
          readMoreText: "Read More"
        },
        {
          image: require('@/assets/images/resource/portfolio-03.jpg'),
          category: "Smart",
          meta: "By Brandon • Jan 10, 2025",
          title: "How AI is Revolutionizing Business and Industry Today",
          readMoreText: "Read More"
        }
      ]
    })

    const aimlSlides = ref([
      {
        backgroundImage: '/banner/banner-three.jpg',
        subtitle: 'AI Innovation',
        titleLines: [
          { text: 'AI Machine ' },
          { text: 'AI', highlight: 'Solutions' }
        ],
        description: 'Transforming businesses with cutting-edge AI and machine learning solutions that drive innovation and growth.',
        buttonText: 'Get Started',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/banner/banner-three.jpg',
        subtitle: 'Machine Learning',
        titleLines: [
          { text: 'Smart ', highlight: 'Automation' }
        ],
        description: 'Leverage the power of machine learning to automate processes, predict outcomes, and make data-driven decisions.',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/banner/banner-three.jpg',
        subtitle: 'Deep Learning',
        titleLines: [
          { text: 'AI ', highlight: 'Intelligence' }
        ],
        description: 'Unlock the potential of deep learning algorithms to solve complex problems and create intelligent systems.',
        buttonText: 'Explore Solutions',
        buttonLink: '/contact'
      }
    ])

    const aimlMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the leading provider of AI and machine learning solutions that transform businesses and drive innovation.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To deliver cutting-edge AI technologies that empower businesses to achieve their goals through intelligent automation.'
      }
    })
    
    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Initialize Swipers
      initCompaniesSwiper()
      initServicesSwiper()
      initTestimonialsSwiper()
    })

    const initCompaniesSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            companiesSwiper.value = new window.Swiper('.companies-slider', {
              loop: true,
              autoplay: {
                delay: 3000,
                disableOnInteraction: false,
              },
              navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              },
              breakpoints: {
                0: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 5,
                  spaceBetween: 30,
                }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    const initServicesSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            servicesSwiper.value = new window.Swiper('.services-slider', {
              loop: true,
              autoplay: {
                delay: 4000,
                disableOnInteraction: false,
              },
              navigation: {
                nextEl: '.services-next',
                prevEl: '.services-prev',
              },
              pagination: {
                el: '.services-pagination',
                clickable: true,
              },
              breakpoints: {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 1,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 1,
                  spaceBetween: 30,
                }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    const initTestimonialsSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            testimonialsSwiper.value = new window.Swiper('.testimonials-slider', {
              loop: true,
              autoplay: {
                delay: 5000,
                disableOnInteraction: false,
              },
              pagination: {
                el: '.testimonials-pagination',
                clickable: true,
              },
              breakpoints: {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    return {
      companiesSwiper,
      servicesSwiper,
      testimonialsSwiper,
      isMonthly,
      aimlSlides,
      aimlMissionVision,
      trustContent,
      howWorkContent,
      servicesContent,
      testimonialsContent,
      pricingContent,
      blogsContent
    }
  }
}
</script>

<style scoped>
/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';
</style> 